import React, { Component } from "react";
import { connect } from "react-redux";
import { Form } from "react-bootstrap";
import SimpleReactValidator from "simple-react-validator";
import { sumBy } from "lodash";
import { bindActionCreators } from "redux";
import {
    fetchOrderHeader,
    submit,
    unSubmit,
    createOrUpdate,
    fetchOrderDetail
} from "../../../actions/orderActions";
import Spinner from "../../common/Spinner";
import OrderDetailTable from "./OrderDetailTable";
import UserTypes from "../../auth/user-types";

const lblUPCErrorId = "lblUPCError";
const lblSubmitErrorId = "lblSubmitError";
const txtUPCId = "txtUPC";
const noCountRequiredError = "There are no order details entered. Either provide some data or check 'No Count Required' to submit the order.";

class OrderEntry extends Component {
    constructor(props) {
        super(props);
        const propsServiceId = this.props.serviceId;
        const serviceId = propsServiceId ||
            parseInt(window.location.href.split("serviceId=")[1], 10);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.onInputBlur = this.onInputBlur.bind(this);
        this.handleIsDeleteChange = this.handleIsDeleteChange.bind(this);
        this.handleCountNeededChange = this.handleCountNeededChange.bind(this);
        this.handleUnSubmit = this.handleUnSubmit.bind(this);

        this.validator = new SimpleReactValidator({
            locale: "en",
            element: message => <div className="text-danger">{message}</div>
        });

        const isMerchandiser = this.props.user.role.includes(UserTypes.merchandiser);

        this.state = {
            runValidation: true,
            serviceId,
            qty: 1,
            upc: "",
            isDelete: false,
            noCountNeeded: false,
            noCountError: "",
            showPropsError: false,
            isMerchandiser
        };
    }

    handleQtyChange(e) {
        this.clearSubmitError();
        this.validateInputs();
        this.validatePropsError();
        this.setState({
            qty: e.target.value
        }, () => {
            this.validator.showMessages();
        });
    }

    handleIsDeleteChange() {
        this.clearSubmitError();
        this.setState({ isDelete: !this.state.isDelete });
    }

    handleUpcChange(e) {
        this.clearSubmitError();
        if (e.key === "Enter") {
            this.onInputBlur(e.target.value);
            return;
        }
        this.validateInputs();
        this.validatePropsError();
        this.setState({
            upc: e.target.value
        }, () => {
            this.validator.showMessages();
        });
    }

    onKeyUp(e) {
        if (e.key === "Enter" && e.target.value) {
            this.onInputBlur(e.target.value);
        }
    }

    handleCountNeededChange() {
        this.clearSubmitError();
        this.setState({ noCountNeeded: !this.state.noCountNeeded });
    }

    componentDidMount() {
        this.clearSubmitError();
        this.props.fetchOrderHeader(this.state.serviceId);
    }

    validateInputs() {
        this.setState({
            runValidation: true
        });
    }

    clearSubmitError() {
        this.setState({
            noCountError: ""
        });
    }

    validatePropsError() {
        this.setState({
            showPropsError: true
        });
    }

    ignoreValidateInputs() {
        this.setState({
            runValidation: false
        });
    }

    handleSubmit() {
        this.validatePropsError();
        this.ignoreValidateInputs();
        const {
            orderHeader,
            orderDetails
        } = this.props;
        const {
            noCountNeeded,
            serviceId
        } = this.state;
        if (orderDetails.length === 0 && !noCountNeeded) {
            this.setState({
                noCountError: noCountRequiredError
            });
            return;
        } else {
            this.clearSubmitError();
        }

        const payload = {
            serviceId,
            orderId: orderHeader.orderId,
            planogramId: orderHeader.planogramId,
            noCountNeeded
        };

        this.props.submit(payload);
    }

    handleUnSubmit() {
        this.ignoreValidateInputs();
        this.clearSubmitError();
        const { orderId } = this.props.orderHeader;
        const { serviceId } = this.state;
        this.props.unSubmit(orderId, serviceId);
        this.setState({
            noCountNeeded: false
        });
        this.validatePropsError();
    }

    handleCancelClick() {
        this.clearSubmitError();

        if (this.props.handleClose) {
            return this.props.handleClose();
        }

        this.props.history.push("/order");
    }

    onInputBlur(value) {
        this.clearSubmitError();
        if (!value) {
            return;
        }

        if (this.validator.allValid()) {
            const {
                qty,
                isDelete,
                serviceId
            } = this.state;
            const {
                planogramId,
                orderId
            } = this.props.orderHeader;

            const payload = {
                orderId,
                serviceId,
                planogramId,
                upc: value,
                quantity: qty,
                isDelete
            };

            this.props.createOrUpdate(payload);

            this.ignoreValidateInputs();
            this.setState({
                qty: 1,
                upc: "",
                isDelete: false
            });
        } else {
            this.validator.showMessages();
        }
    }

    errorRenderer(upcError, submitError, noCountError) {
        const { showPropsError } = this.state;
        const message = [];
        if (showPropsError && upcError) {
            message.push(<div
                className="text-danger"
                id={lblUPCErrorId}
            >
                {upcError}
            </div>);
        }
        if (showPropsError && submitError) {
            message.push(<div
                className="text-danger"
                id={lblSubmitErrorId}
            >
                {submitError}
            </div>);
        }
        if (noCountError) {
            message.push(<div
                className="text-danger"
                id={lblSubmitErrorId}
            >
                {noCountError}
            </div>);
        }

        return message;
    }

    render() {
        const {
            orderHeader,
            orderDetails,
            orderDetailAddOrUpdateError,
            orderSubmitError
        } = this.props;

        const maxHeight = this.props.serviceId ? "75vh" : "100vh";

        const planogramCode = orderHeader ? orderHeader.planogramCode : "";
        const jobDescription = orderHeader ? orderHeader.jobDescription : "";
        const storeNumber = orderHeader ? orderHeader.storeNumber : "";
        const isOrderSubmitted = orderHeader ? orderHeader.isOrderSubmitted : false;

        const {
            qty,
            isDelete,
            upc,
            serviceId,
            noCountNeeded,
            runValidation,
            noCountError,
            isMerchandiser
        } = this.state;

        const totalItems = sumBy(orderDetails, "quantity");
        const cost = sumBy(orderDetails, "cost");
        const retail = sumBy(orderDetails, "retail");

        return (
            <div
                className="dashboard px-5 position-relative pre-scrollable max"
                style={{ maxHeight }}
            >
                <h3 className="mb-4">
                    Inventory Scan (no Layout)
                </h3>

                <div className="row pt-1 pb-3">
                    <div className="col-4 pt-3">
                        <fieldset style={{
                            maxWidth: "400px",
                            minHeight: "167px"
                        }} className="border pt-2 p-2">
                            <div style={{ marginTop: "30px" }}>
                                <div className="custom-control custom-checkbox mb-2 mt-4">
                                    <span style={{
                                        fontWeight: "bold",
                                        marginRight: "5px"
                                    }}>Planogram:</span>
                                    <span>
                                    {planogramCode}
                                </span>
                                </div>
                                <div className="custom-control custom-checkbox mb-2">
                                    <span className="mr-2"
                                          style={{ fontWeight: "bold" }}>#Job:</span>
                                    <span>
                                    {jobDescription}
                                </span>
                                </div>
                                <div className="custom-control custom-checkbox mb-2">
                                    <span className="mr-2"
                                          style={{ fontWeight: "bold" }}>#Store:</span>
                                    <span>
                                    {storeNumber}
                                </span>
                                </div>
                            </div>

                        </fieldset>
                    </div>

                    <div className="col-4 d-flex">
                        {
                            !isOrderSubmitted ?
                                <div className="row float-right">
                                    <div className="col-12 mb-2">
                                        <Form.Group
                                            className="align-items-center d-flex form-group float-right">
                                            <Form.Label className="text-indent">Delete</Form.Label>
                                            <Form.Check
                                                name="isDelete"
                                                checked={isDelete}
                                                label="Delete"
                                                id="isDelete"
                                                onChange={this.handleIsDeleteChange}
                                            />

                                        </Form.Group>
                                    </div>
                                    <div className="col-12 mb-2">
                                        <div className="custom-control custom-checkbox mb-2">
                                            <div className="input-group">
                                                <span className="mr-2"
                                                      style={{ fontWeight: "bold" }}>QTY:</span>
                                                <input
                                                    className="form-control"
                                                    type="number"
                                                    value={qty}
                                                    onChange={e => this.handleQtyChange(e)}
                                                />

                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 mb-2">
                                        <div className="custom-control custom-checkbox mb-2">
                                            <div className="input-group">
                                                <span className="mr-2"
                                                      style={{ fontWeight: "bold" }}>UPC:</span>
                                                <input
                                                    id={txtUPCId}
                                                    className="form-control"
                                                    type="text"
                                                    value={upc}
                                                    onBlur={ev => this.onInputBlur(ev.target.value)}
                                                    onChange={e => this.handleUpcChange(e)}
                                                    onKeyUp={ev => this.onKeyUp(ev)}
                                                />
                                            </div>
                                            <div className="mb-1" style={{ minHeight: "50px" }}>
                                                <div>
                                                    {!runValidation || this.validator.message("qty", qty, "required|numeric|min:1,num")}
                                                    {!runValidation || this.validator.message("upc", upc, "required")}
                                                </div>
                                                {this.errorRenderer(orderDetailAddOrUpdateError, orderSubmitError, noCountError)}
                                            </div>
                                        </div>
                                    </div>
                                </div> :
                                <div>
                                    <h3 style={{
                                        color: "#bd2130",
                                        fontWeight: "bold",
                                        marginTop: "10px"
                                    }}>Submitted</h3>
                                    <div className={"mt-5"}>
                                        {this.errorRenderer(orderDetailAddOrUpdateError, orderSubmitError, noCountError)}
                                    </div>
                                </div>

                        }
                    </div>

                    <div className="col-4 d-flex flex-column">
                        <div className="row float-right">
                            <div className="col-12 mb-2">
                                <Form.Group
                                    className="align-items-center d-flex form-group float-right">
                                    <Form.Label className="text-indent">Count</Form.Label>
                                    <Form.Check
                                        name="noCountRequired"
                                        disabled={isOrderSubmitted}
                                        checked={noCountNeeded}
                                        label="No Count Required"
                                        id="noCountRequired"
                                        onChange={this.handleCountNeededChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-12">
                                {
                                    // eslint-disable-next-line no-nested-ternary
                                    isOrderSubmitted ?
                                        (isMerchandiser ? null :
                                            <button
                                                style={{ width: "120px" }}
                                                className="btn btn-danger mb-2 float-right"
                                                onClick={this.handleUnSubmit}
                                            >
                                                UnSubmit
                                            </button>) :
                                        <button
                                            style={{ width: "120px" }}
                                            className="btn btn-success mb-2 float-right"
                                            onClick={this.handleSubmit}
                                        >
                                            Submit
                                        </button>
                                }
                            </div>
                            <div className="col-12">
                                <button
                                    style={{ width: "120px" }}
                                    className="btn btn-outline-info mb-2 float-right"
                                    onClick={e => this.handleCancelClick(e)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 d-flex flex-column">
                    <div className="row float-right">
                        <div className="col-12 mb-2">
                            <span style={{ float: "right" }}>
                                Total Items {totalItems}, Cost ${cost}, Retail ${retail}
                            </span>
                        </div>
                    </div>
                </div>

                {!this.props.loading || <Spinner/>}
                <OrderDetailTable
                    orderDetails={this.props.orderDetails}
                    isOrderSubmitted={isOrderSubmitted}
                    serviceId={serviceId}
                />
            </div>
        );
    }
}

const mapStateToProps = state => ({
    user: state.auth.user,
    orderHeader: state.order.orderHeader,
    loading: state.order.loading,
    submit: state.order.submit,
    orderDetails: state.order.orderDetails,
    orderDetailAddOrUpdate: state.order.orderDetailAddOrUpdate,
    orderSubmit: state.order.orderSubmit,
    orderSubmitError: state.order.orderSubmitError,
    isSubmitted: state.order.isSubmitted,
    orderDetailAddOrUpdateError: state.order.orderDetailAddOrUpdateError
});

const mapStateToDispatch = dispatch => ({
    fetchOrderHeader: bindActionCreators(fetchOrderHeader, dispatch),
    submit: bindActionCreators(submit, dispatch),
    unSubmit: bindActionCreators(unSubmit, dispatch),
    createOrUpdate: bindActionCreators(createOrUpdate, dispatch),
    fetchOrderDetail: bindActionCreators(fetchOrderDetail, dispatch)
});

export default connect(
    mapStateToProps,
    mapStateToDispatch
)(OrderEntry);
